import { TaskType } from "@common/types/task";
import { Transaction } from "@common/types/transaction";
import { PersistableView } from "@common/types/views/views";
import { TransactionView } from "@common/types/views/transactionViews";
import { MatchableItem } from "@components/accounting/transactionMatch/types";
import { TransactionViewDrawerProps } from "@components/transactions/TransactionViewDrawer/TransactionViewDrawer";
import { RuleGroupType, RuleSource } from "@common/types/transactionRule";
import { NewTransactionRuleDefinition } from "@components/transactionRules/TransactionRuleCreation/TransactionRuleCreationDrawer";
import { TransactionFiltersDto } from "@common/dto/transactions/getTransactions/transaction-filters.dto";
import { BankReconciliation } from "@common/contracts/reconciliation.contract";
import { ScheduleLifecyclePeriodLockedError } from "@common/schemas/domains/accounting/scheduleLifecycle.schema";
import {
    AccountClass,
    AccountSubtype,
    AccountType,
} from "@common/constants/domains/accounting/accounts";
import { Account } from "@common/types/domains/accounting/account";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { SelectedTransaction } from "@components/transactions/TransactionsBulkActions/transactionsBulkActionsContext";
import { ExtendedAccount } from "@common/types/reports/types";
import { BulkUpdateParams } from "@components/transactions/TransactionsBulkActions/lib";
import { TaskCreate } from "@common/contracts/task.contract";
import { Entity } from "@common/types/entity";
import { Category } from "@common/types/category";
import { DocumentCategory } from "@common/types/documentCategory";
import { Organization } from "@common/types/organization";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { WorkspaceMember } from "@common/types/workspace";
import { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import { OrganizationUserRole } from "@common/userOrganizationRoles";
import { OrganizationMember } from "@common/dto/organization/organization-member.dto";
import {
    AddMemberOptions,
    MemberManagementOverlayEntity,
} from "@components/memberManagement/memberManagement.types";
import { OrganizationIdNameLogoUrl } from "@common/schemas/organization.schema";
import { EnrollAction } from "@components/organizationView/organizationBilling/lib";
import { AccrualImporterRow } from "@components/accrualImporter/types";
import { AccrualDocumentImporterMode } from "@components/accrualDocumentImporter/accrualDocumentImporter.lib";
import { PerEntityTaskConfig } from "@components/drawers/taskCreation.types";
import { ConsolidationEligibilityBody } from "@common/schemas/domains/accounting/consolidation.schema";
import { NativeInvoiceCreatePrefill } from "@components/invoices/NativeInvoiceDrawer/nativeInvoiceFormTypes";
import { InvoiceFilters } from "@common/contracts/invoice.contract";
import { BillFilters } from "@common/contracts/bill.contract";
import { JournalEntryDuplicateDraft } from "@components/accounting/journalEntryDrawer/JournalEntryDrawer.lib";
import { ReportSettingsEditorSubject } from "@components/settings/ReportSettings/reportSettingsEditor";
import { createContext, useCallback, useContext, useMemo } from "react";
import { SubScopeContext, SubScopeValue } from "@state/subScopeContext.lib";

// Here defined the key to the global drawer and the value the data you want to pass down
interface OverlayConfig {
    enrollOrganization?: {
        action?: EnrollAction;
    };
    memberManagementAddMember?: {
        workspaceId?: string;
        actingUserRole?: UserWorkspaceRole;
        entities?: MemberManagementOverlayEntity[];
        addMemberHeader?: React.ReactNode;
        options?: AddMemberOptions;
    };
    memberManagementConfigurePermissions?: {
        member: WorkspaceMember;
        onClose?: () => void;
        workspaceId?: string;
        actingUserRole?: UserWorkspaceRole;
        entities?: MemberManagementOverlayEntity[];
        configurePermissionsHeader?: React.ReactNode;
    };
    memberManagementConfirmOrganizations?: {
        organizations: OrganizationIdNameLogoUrl[];
        onConfirm: () => Promise<void>;
        onBackToAddMember?: OverlayConfig["memberManagementAddMember"];
        onClose?: () => void;
    };
    organizationAddTeamMember?: {
        organization: Organization;
        email?: string;
        role?: OrganizationUserRole;
        onInvited?: () => void;
        initialWorkspaceIds?: string[];
    };
    organizationConfigureMemberPermissions?: {
        organization: Organization;
        member: OrganizationMember;
    };
    /**
     * The accounts of a connection just made from the organization view, to be
     * given a client and an entity. Global rather than owned by the
     * integrations page: the connect flow runs inside the command palette,
     * which unmounts as soon as it closes.
     */
    organizationPostConnectAssignment?: {
        organizationId: string;
        /** Unified connection id — the `login` group these accounts sit in. */
        connectionId: string;
    };
    createTask?: {
        type: TaskType;
        transactionsTask?: {
            transactions?: SelectedTransaction[];
            transactionsCount?: number;
        };
        viewConfig?: PersistableView;
        initialValues?: Partial<Omit<TaskCreate, "type">>;
        relatedTaskId?: string;
        perEntityTask?: PerEntityTaskConfig;
    };
    reconcilieFinancialAccount?: {
        financialAccountId: number;
        previousReconciliation?: BankReconciliation;
        onSuccess?: () => void;
    };
    globalTransactionView?: Omit<
        TransactionViewDrawerProps,
        "open" | "onClose"
    >;
    financialDocumentId?: number;
    /**
     * Opens the full document details modal (preview + matches/details),
     * identical to the modal shown from the Documents page — as opposed to
     * `financialDocumentId`, which opens the lightweight side drawer used by
     * attachment pills elsewhere. Used by chat document reference chips.
     */
    financialDocumentDetailsModal?: number;
    /** A schedule apply or lifecycle action refused for locked periods. */
    scheduleLockedPeriods?: ScheduleLifecyclePeriodLockedError;
    createRule?: {
        definition?: NewTransactionRuleDefinition;
        source: RuleSource;
        groupType: RuleGroupType;
    };
    addTransactions?: boolean;
    recordCheck?: {
        financialAccountId?: number;
    };
    recordDeposit?: {
        financialAccountId?: number;
    };
    importer?: boolean;
    transactionSplit?: {
        transaction: TransactionListItem;
        onSplitSave?: (savedTransactions: Transaction[]) => void;
    };
    similarTransactionsUpdate?: {
        filters: TransactionFiltersDto;
        valueToAssign: BulkUpdateParams;
        displayedChangedEntityName?: string;
        view: TransactionView;
        updatedTransaction: Transaction;
        totalTransactions: number;
    };
    createCoaAccount?: {
        entityIds?: number[];
        accountName: string;
        accountType: AccountType;
        accountSubtype: AccountSubtype;
        parentGroupId?: string | null;
        availableClassifications?: AccountClass[];
        onAccountsCreated?: (accounts: Account[]) => void;
    };
    disableChartOfAccounts?: {
        accounts: ExtendedAccount[];
    };
    enableChartOfAccounts?: {
        accounts: ExtendedAccount[];
    };
    deleteChartOfAccounts?: {
        accounts: ExtendedAccount[];
    };
    mergeChartOfAccounts?: {
        account: ExtendedAccount;
    };
    reassignAccountRole?: {
        account: ExtendedAccount;
    };
    deleteCoaGroup?: {
        name: string;
        groups: Array<{ id: string; entityId: number }>;
    };
    referralDrawer?: boolean;
    accountingMigration?: boolean;
    reportSettingsEditor?: ReportSettingsEditorSubject & {
        restoreSettings?: () => void;
    };
    quickbooksMapAccounts?: {
        connectionWorkspaceId: string;
        entityId: number;
        nextTaskId?: string;
    };
    coaMapping?: {
        connectionWorkspaceId: string;
        entityId: number;
        nextTaskId?: string;
    };
    journalEntryDrawer?: {
        entity: Pick<Entity, "id" | "legalType" | "name">;
        journalEntryId?: string;
        ledgerBasis?: AccountingLedgerBasis;
        onClose?: () => void;
        ledgerId?: string;
        /** Pre-fills the date field when creating a new journal entry (used by Save and New). */
        initialDate?: string;
        /** Pre-fills a new journal entry with a copy of a saved manual journal entry (used by Duplicate). */
        duplicateFrom?: JournalEntryDuplicateDraft;
    };
    createCategory?: {
        categoryName?: string;
        parentCategoryId?: string;
        onCategoryCreated?: (category: Category) => void;
    };
    updateCategory?: {
        category: Category;
    };
    deleteCategory?: {
        category: Category;
        label: string;
        transactionsCount: number;
    };
    createDocumentCategory?: {
        categoryName?: string;
        parentCategoryId?: string;
        onCategoryCreated?: (category: DocumentCategory) => void;
    };
    billLineItemsDrilldown?: {
        billId: string;
        entityIds: number[];
    };
    invoiceLineItemsDrilldown?: {
        invoiceId: string;
        entityIds: number[];
    };
    invoicesDrilldown?: {
        filters: InvoiceFilters;
        // Invoice picker used by "Create template" → "From invoice":
        // search/filter the list and select an invoice to seed a new template.
        selectForTemplate?: boolean;
    };
    invoiceTemplatesDrilldown?: {
        catalogItemIds: string[];
    };
    billsDrilldown?: {
        filters: BillFilters;
    };
    transactionMatch?: {
        transactionId: number;
        suggestedItems?: MatchableItem[];
    };
    documentAccrualMatch?: {
        documentType: "bill" | "invoice";
        documentId: string;
    };
    manualBillCreation?: { initialRows?: AccrualImporterRow[] };
    manualInvoiceCreation?: { initialRows?: AccrualImporterRow[] };
    manualCashItemImport?: {
        financialAccountId?: number;
    };
    /** Opens the counterparty editor drawer for an existing counterparty. */
    counterpartyEditorDrawer?: {
        counterpartyId: string;
    };
    nativeInvoiceDrawer?: {
        // Edit when set alone. With createTemplate, hydrates a new template
        // from this invoice's snapshot instead of opening the invoice for edit.
        invoiceId?: string;
        // Opens a create-mode drawer pre-filled with ad-hoc line items (name +
        // amount, no catalog item required) and other form fields, so a
        // caller (e.g. "create invoice from these billable expenses") can
        // open straight into a ready-to-save draft. Ignored when invoiceId is
        // set (edit mode always hydrates from the persisted invoice).
        createPrefill?: NativeInvoiceCreatePrefill;
        applyTemplate?: { templateId: string; entityId: number };
        editTemplate?: { templateId: string; entityId: number };
        createTemplate?: boolean;
        // Opens a create-template drawer pre-filled from an existing
        // template's content (name, lines, terms, email, etc.), so the user
        // can save it as a brand-new, independent template.
        duplicateTemplate?: { templateId: string; entityId: number };
    };
    accrualDocumentImporter?: { mode: AccrualDocumentImporterMode };
    consolidationWorkflow?: {
        initialSelection?: ConsolidationEligibilityBody;
    };
    addClient?: boolean;
    addEntity?: { workspaceId: string };
    legacyPlusEntityLimit?: boolean;
    multiEntityUpgrade?: boolean;
}

export type OverlayType = keyof OverlayConfig;
export type OverlayData<T extends OverlayType> = OverlayConfig[T];
export type OverlaysData = {
    [K in OverlayType]?: {
        type: K;
        data?: OverlayConfig[K];
        instanceId: number;
    };
};

interface OverlaysContextType {
    getOverlayInfo: <T extends OverlayType>(
        type: T,
    ) => { type: T; data?: OverlayData<T>; instanceId: number } | undefined;
    openOverlay: <T extends OverlayType>(
        type: T,
        data: OverlayData<T>,
        subScope?: SubScopeValue | null,
    ) => void;
    closeOverlay: () => void;
    overlayIsClosing: boolean;
}

export const OverlaysContext = createContext<OverlaysContextType | null>(null);

type OpenOverlay = <T extends OverlayType>(
    type: T,
    data: OverlayData<T>,
) => void;

type UseOverlaysResult = Omit<OverlaysContextType, "openOverlay"> & {
    openOverlay: OpenOverlay;
};

export const useOverlays = (): UseOverlaysResult => {
    const context = useContext(OverlaysContext);
    const subScope = useContext(SubScopeContext);

    const openOverlay = useCallback<OpenOverlay>(
        (type, data) => context?.openOverlay(type, data, subScope),
        [context, subScope],
    );

    return useMemo(() => {
        if (context === null) {
            throw new Error(
                "useOverlays must be used within a OverlayProvider",
            );
        }

        return { ...context, openOverlay };
    }, [context, openOverlay]);
};
